import React, { useContext, useEffect, useRef } from 'react';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { createClientMessage } from 'react-chatbot-kit';
import { CHAT_TAB_VIEWS } from '../types';
import { APIContext } from '../contexts/APIContext';
import { SessionContext } from '../contexts/SessionContext';
import {
  resetChatbot,
  userMessageHelper,
  userActionHelper,
  chatResponseHelper,
  stepResponseHelper,
  cancelChatResponseHelper,
  handlePostCancelResponseHelper,
} from './chatUtils';
import { getUserEnterpriseId, trackUserClicks, fetchSearchTranscriptDetails } from '../../src/utils';
import { suggestedResponsesImg } from '../chatbot/chatUtils';
import { getValidToken } from '../utils';

// Action provider handles the inputted message
/* eslint-disable react/prop-types */
const ActionProvider = ({ createChatBotMessage, setState, children }) => {
  const {
    resetApp,
    setResetApp,
    selectedResponseId,
    setSelectedResponseId,
    setResponseDetailsSources,
    sourceHistory,
    setSourceHistory,
    searchBatchesHistory,
    setSearchBatchesHistory,
    setStoryResponses,
    chatHistory,
    setChatHistory,
    setActiveChatTab,
    showCitation,
    setShowCitation,
    setGenArcPromptAnalyticsUId,
    setGenArcHistoryPrompts,
    chatPromptResponseHistory,
    setChatPromptResponseHistory,
    selectedProject,
    selectedTranscript,
    chatWindowConversations,
    setChatWindowConversations,
    isChatHistoryActive,
    setIsChatHistoryActive,
    setShowRightPanel,
    setShowChatHistoryPanel,
    prompt,
    setPrompt,
    currentGenArcPromptAnalyticsUId,
    setCurrentGenArcPromptAnalyticsUId,
    isPromptCancelling,
    setIsPromptCancelling,
    instanceId,
    setInstanceId,
    promptCancellationStatus,
    setPromptCancellationStatus,
  } = useContext(ChatbotContext);

  let userMessage = useRef(''); // User message tracker

  // Websocket and API message from context
  const {
    websocketConnectionRef: { current: websocket },
    chatResponse,
    setExportStoryInProgress,
    userDetails,
    chatSessionId,
    selectedHistoryPromptItem,
    setSelectedHistoryPromptItem,
    setChatResponse,
    WBUId,
    AppName,
    accounts,
    batchType,
    setBatchType,
    searchBatches,
    setSearchBatches,
    searchResponses,
    setSearchResponses,
  } = useContext(APIContext);

  // user from session context
  const {
    openAI,
    isPromptRetried,
    setIsPromptGenerating,
    setDownloadChatHistory,
    genAI,
    promptRetriedSource,
    setPromptRetriedSource,
    selectedSuggestion,
    setSelectedSuggestion,
    setIsUpdatingChatHistory,
  } = useContext(SessionContext);
  // Holds all the resources needed to complete Chatbot operations
  const chatbotResources = {
    createChatBotMessage: createChatBotMessage,
    createClientMessage: createClientMessage,
    setState: setState,
    resetApp: resetApp,
    setResetApp: setResetApp,
    setResponseDetailsSources: setResponseDetailsSources,
    sourceHistory: sourceHistory,
    setSourceHistory: setSourceHistory,
    searchBatchesHistory: searchBatchesHistory,
    setSearchBatchesHistory: setSearchBatchesHistory,
    setStoryResponses: setStoryResponses,
    chatHistory: chatHistory,
    setChatHistory: setChatHistory,
    setActiveChatTab: setActiveChatTab,
    userMessage: userMessage,
    selectedResponseId: selectedResponseId,
    setSelectedResponseId: setSelectedResponseId,
    websocket: websocket,
    chatResponse: chatResponse,
    setExportStoryInProgress: setExportStoryInProgress,
    setGenArcHistoryPrompts: setGenArcHistoryPrompts,
    setDownloadChatHistory: setDownloadChatHistory,
    setChatPromptResponseHistory: setChatPromptResponseHistory,
    selectedProject: selectedProject,
    selectedTranscript: selectedTranscript,
    chatWindowConversations: chatWindowConversations,
    setChatWindowConversations: setChatWindowConversations,
    setChatResponse: setChatResponse,
    isChatHistoryActive: isChatHistoryActive,
    setIsChatHistoryActive: setIsChatHistoryActive,
    prompt: prompt,
    setPrompt: setPrompt,
    instanceId: instanceId,
    setInstanceId: setInstanceId,
    currentGenArcPromptAnalyticsUId: currentGenArcPromptAnalyticsUId,
    setCurrentGenArcPromptAnalyticsUId: setCurrentGenArcPromptAnalyticsUId,
    isPromptCancelling: isPromptCancelling,
    setIsPromptCancelling: setIsPromptCancelling,
    promptCancellationStatus: promptCancellationStatus,
    setPromptCancellationStatus: setPromptCancellationStatus,
    //setGenArcPromptAnalyticsUId: setGenArcPromptAnalyticsUId,
  };

  // to get userInfo
  // const { accounts } = useMsal();

  // USE EFFECTS =========================================================================>

  // // Add an event listener to the document to listen for clicks outside of the popup
  useEffect(() => {
    window.addEventListener('suggested-response-btn-clicked', showSuggestionsPopup);
    window.addEventListener('suggestion-clicked', closeSuggestionsPopup);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('suggested-response-btn-clicked', showSuggestionsPopup);
      window.removeEventListener('suggestion-clicked', closeSuggestionsPopup);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Use effect to receive API message from webhook
  useEffect(() => {
    if (chatResponse) handleChatResponse(chatResponse); // need to parse to JSON
  }, [chatResponse]);

  // Use effect for resetting the chat
  useEffect(() => {
    if (resetApp) resetChatbot(chatbotResources);
  }, [resetApp]);

  // useEffect to watch for changes in selectedResponseId
  useEffect(() => {
    if (selectedResponseId !== null) {
      const selectedSources = sourceHistory[selectedResponseId];
      const selectedSearchBatches = searchBatchesHistory[selectedResponseId];
      if (selectedSources) {
        setResponseDetailsSources(selectedSources);
        setShowCitation(selectedSources?.showCitation);
        // Swap the chat tab back to the Response Details tab
        setActiveChatTab(CHAT_TAB_VIEWS.RESPONSE_DETAILS);
      } else {
        setResponseDetailsSources(selectedSources);
        console.log('No sources found for this message.');
      }
      if (selectedSearchBatches) {
        setSearchBatches(selectedSearchBatches.batches || []);
        setBatchType(selectedSearchBatches.batchType || '');
        setSearchResponses(selectedSearchBatches.searchResponses || {});
      }
    }
  }, [selectedResponseId, sourceHistory]);

  // useEffect to change right panel and history panel based on citation flag
  useEffect(() => {
    setShowChatHistoryPanel((prevValue) => (showCitation ? false : prevValue));
    setShowRightPanel(showCitation);
  }, [showCitation]);

  useEffect(() => {
    if (batchType === 'complete' && searchBatches.length > 0 && selectedResponseId) {
      setSearchBatchesHistory((prevHistory) => {
        const existingEntry = prevHistory[selectedResponseId];
        if (existingEntry) {
          return prevHistory;
        }
        return {
          ...prevHistory,
          [selectedResponseId]: {
            batches: searchBatches,
            batchType: batchType,
            searchResponses: searchResponses,
          },
        };
      });
    }
  }, [batchType, selectedResponseId]);

  /*
  Side Effect which triggers when response for chat history is received.
  Triggers when selectedHistoryPromptItem changes
  */
  useEffect(() => {
    if (Object.keys(selectedHistoryPromptItem)?.length) {
      handleChatHistory();
    }
  }, [selectedHistoryPromptItem]);

  // side effect to handle the click of suggested prompts/starter questions to trigger API call.
  useEffect(() => {
    let timeout;
    if (selectedSuggestion !== '') {
      timeout = setTimeout(() => {
        handleUserAction(selectedSuggestion);
        setSelectedSuggestion('');
      }, 1000);
    }

    return () => clearTimeout(timeout); // clear timeout when component unmounts to avoid unwanted prompt triggers
  }, [selectedSuggestion, setSelectedSuggestion]);

  /*
  Side effect updates the internal chat bot state with stored conversations.
  resets the chatbot with starter questions when there is no conversations to load/show. 
  Triggers when chatWindowConversations changes
  */
  useEffect(() => {
    if (chatWindowConversations.length) {
      setState((state) => {
        return { ...state, messages: [...chatWindowConversations] };
      });
    } else {
      resetChatbot(chatbotResources);
    }
  }, [chatWindowConversations]);

  useEffect(() => {
    if (isPromptCancelling === true) {
      /*
      Executes if prompt cancel is triggered by user
      */
      handlePromptCancel();
    } else if (isPromptCancelling === false && instanceId && currentGenArcPromptAnalyticsUId) {
      /*
      Executes only post prompt cancellation response and when instanceId and currentGenArcPromptAnalyticsUId are available.
      */
      handlePostPromptCancel();
    }
  }, [isPromptCancelling]);

  // END OF USE EFFECTS ==================================================================>
  // HANDLERS AND HELPERS ================================================================>

  // Function to handle the user message in the chat
  const handleUserMessage = async (message, callWithOpenAI, callWithGenAI, promptRetried = false) => {
    // Clear previous response details and search batches when sending new prompt
    const token = await getValidToken();
    setResponseDetailsSources({ showCitation: false, sourceList: [] });
    setBatchType('');
    setSearchBatches([]);
    if (promptRetried == true) {
      // Create message bubble
      const clientMessage = createClientMessage(message);
      // Update the state to show message bubble
      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, clientMessage],
        showResponseSugestionsPopup: false,
      }));
      // Generate separate UUIDs for chat and search APIs
      const chatGenArcPromptAnalyticsUId = crypto.randomUUID();
      const searchGenArcPromptAnalyticsUId = crypto.randomUUID();
      userMessageHelper(
        chatbotResources,
        message,
        getUserEnterpriseId(accounts),
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        callWithOpenAI,
        callWithGenAI,
        chatSessionId,
        chatGenArcPromptAnalyticsUId,
        false,
        setSearchBatches,
        setBatchType,
        token
      );
      fetchSearchTranscriptDetails(
        accounts,
        websocket,
        message,
        selectedProject,
        callWithOpenAI,
        callWithGenAI,
        selectedTranscript,
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        searchGenArcPromptAnalyticsUId,
        false,
        token
      );
      setIsPromptGenerating(true);
    } else {
      //Manually Cceating the message bubble with the prompt because of custom chat input box implementation.
      const clientMessage = createClientMessage(message);
      setState((prev) => {
        let newMessages = [];
        if (prev.messages.length > 0 && prev.messages[prev.messages.length - 1].message === '') {
          newMessages = [...prev.messages.slice(0, -1), clientMessage];
        }
        return { ...prev, messages: newMessages.length ? newMessages : [...prev.messages, clientMessage] };
      });
      // Generate separate UUIDs for chat and search APIs
      const chatGenArcPromptAnalyticsUId = crypto.randomUUID();
      const searchGenArcPromptAnalyticsUId = crypto.randomUUID();
      userMessageHelper(
        chatbotResources,
        message,
        getUserEnterpriseId(accounts),
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        openAI,
        genAI,
        chatSessionId,
        chatGenArcPromptAnalyticsUId,
        false,
        setSearchBatches,
        setBatchType,
        token
      );
      // Call search API along with chat API
      fetchSearchTranscriptDetails(
        accounts,
        websocket,
        message,
        selectedProject,
        openAI,
        genAI,
        selectedTranscript,
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        searchGenArcPromptAnalyticsUId,
        false,
        token
      );
      setIsPromptGenerating(true);
    }
  };

  //Function to handle chathistory(req, res), handling chatbot ui changes
  const handleChatHistory = () => {
    const userPromptMsg = createClientMessage(selectedHistoryPromptItem.promptName);
    const botResponseMsg = createChatBotMessage(
      { ...selectedHistoryPromptItem.chatResponse },
      {
        widget: 'chatHistoryActiveChatWidget',
        payload: {
          chatResponse: { ...selectedHistoryPromptItem.chatResponse }, // send chat response to payload
        }, // Sending API response as payload for static use
        withAvatar: true,
      }
    );

    const messages = [userPromptMsg, botResponseMsg];
    /**
     * Update the genArcHistoryPrompts object with the new prompt message from selected chat history item.
     * Call the setGenArcHistoryPrompts function to update the genArcHistoryPrompts state with the updated object
     */
    setGenArcHistoryPrompts((genArcHistoryPrompts) => ({
      ...genArcHistoryPrompts,
      [selectedHistoryPromptItem?.chatResponse?.responseId]: selectedHistoryPromptItem.promptName,
    }));

    /*
    update ChatWindowConversations state with history prompt and its response. which indirectly updates the internal chatbot state which handled in useEffects 
    */
    setChatWindowConversations([...messages]);
    //update the isChatHistoryActive state to true
    setIsChatHistoryActive(true);

    //reset the selectedHistoryPromptItem once after history prompt response is received
    //helps in avoid conflicts
    if (Object.keys(selectedHistoryPromptItem).length > 1) {
      setSelectedHistoryPromptItem({});
      setIsUpdatingChatHistory({ state: false, action: '' }); // remove loader when history prompt response is received
    }
  };

  // Function to handle a user response (by suggestion click or voice)
  const handleUserAction = async (message, callWithOpenAI, callWithGenAI, promptRetried = false) => {
    const token = await getValidToken();
    // Clear previous response details and search batches when sending new prompt
    setResponseDetailsSources({ showCitation: false, sourceList: [] });
    setBatchType('');
    setSearchBatches([]);
    if (promptRetried == true) {
      // Generate separate UUIDs for chat and search APIs
      const chatGenArcPromptAnalyticsUId = crypto.randomUUID();
      const searchGenArcPromptAnalyticsUId = crypto.randomUUID();
      userActionHelper(
        chatbotResources,
        message,
        getUserEnterpriseId(accounts),
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        callWithOpenAI,
        callWithGenAI,
        chatSessionId,
        chatGenArcPromptAnalyticsUId,
        setSearchBatches,
        setBatchType,
        token
      );
      fetchSearchTranscriptDetails(
        accounts,
        websocket,
        message,
        selectedProject,
        callWithOpenAI,
        callWithGenAI,
        selectedTranscript,
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        searchGenArcPromptAnalyticsUId,
        true,
        token
      );
      setIsPromptGenerating(true);
    } else {
      // Generate separate UUIDs for chat and search APIs
      const chatGenArcPromptAnalyticsUId = crypto.randomUUID();
      const searchGenArcPromptAnalyticsUId = crypto.randomUUID();
      userActionHelper(
        chatbotResources,
        message,
        getUserEnterpriseId(accounts),
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        openAI,
        genAI,
        chatSessionId,
        chatGenArcPromptAnalyticsUId,
        setSearchBatches,
        setBatchType,
        token
      );
      /**
       * set the prompt message to the prompt state,
       * when triggered from suggestions or suggestions pop-up
       */
      setPrompt(message);
      fetchSearchTranscriptDetails(
        accounts,
        websocket,
        message,
        selectedProject,
        openAI,
        genAI,
        selectedTranscript,
        userDetails?.ConfigurationDetails?.PredefinedPrompts,
        searchGenArcPromptAnalyticsUId,
        true,
        token
      );
      setIsPromptGenerating(true);
    }
  };

  // Function to handle the Chat response
  const handleChatResponse = async (parsedResponse) => {
    const token = await getValidToken();
    // Ignore search API payload
    if (!parsedResponse?.chatResponse && !parsedResponse?.stepResponse) {
      return;
    }
    // General chat response
    if (parsedResponse?.chatResponse) {
      // We got a final response
      //Update the chat history with new prompts and its details
      const title = userMessage.current;
      const response = parsedResponse.chatResponse.text;
      const source = parsedResponse.chatResponse.responseSource;
      const history = chatPromptResponseHistory;
      const genArcPromptAnalyticsUId = parsedResponse?.chatResponse?.GenArcPromptAnalyticsUId;
      history.push({
        title: title,
        response: response,
        source: source,
        SelectedProjects: selectedProject,
        genArcPromptAnalyticsUId: genArcPromptAnalyticsUId,
      });

      chatResponseHelper(chatbotResources, parsedResponse);
      setChatPromptResponseHistory(history);
      setIsPromptGenerating(false);
      setBatchType('complete');
      setShowCitation(parsedResponse?.chatResponse?.show_citation);
      setGenArcPromptAnalyticsUId((genArcPromptAnalyticsUId) => ({
        ...genArcPromptAnalyticsUId,
        [parsedResponse?.chatResponse?.responseId]: parsedResponse?.chatResponse?.GenArcPromptAnalyticsUId,
      }));
      trackUserClicks(
        websocket,
        'GMA chat prompt',
        userDetails,
        chatSessionId,
        WBUId,
        AppName,
        accounts,
        token,
        parsedResponse?.chatResponse?.GenArcPromptAnalyticsUId,
        isPromptRetried ? (promptRetriedSource == 'openAI' ? true : false) : openAI,
        isPromptRetried ? (promptRetriedSource == 'genAI' ? true : false) : genAI
      );
      setPromptRetriedSource('');
      setPrompt('');
    } else {
      // We got a step response
      stepResponseHelper(chatbotResources, parsedResponse);
    }
  };

  const handlePromptCancel = async () => {
    const token = await getValidToken();
    cancelChatResponseHelper(
      chatbotResources,
      getUserEnterpriseId(accounts),
      userDetails?.ConfigurationDetails?.PredefinedPrompts,
      openAI,
      genAI,
      chatSessionId,
      token
    );
    trackUserClicks(
      websocket,
      'PromptCancellation',
      userDetails,
      chatSessionId,
      WBUId,
      AppName,
      accounts,
      token,
      currentGenArcPromptAnalyticsUId
    );
  };

  const handlePostPromptCancel = () => {
    setIsPromptGenerating(false);
    setBatchType('complete');
    setPromptRetriedSource('');
    setPrompt('');
    handlePostCancelResponseHelper(chatbotResources);
  };

  // Function to handle clicks outside of the popup
  const handleClickOutside = (event) => {
    const popupElement = document.getElementById('response-suggestions-popup');
    if (popupElement && !popupElement.contains(event.target)) {
      suggestedResponsesImg.setAttribute('src', '/icons/BulbIcon.svg');
      setState((prev) => ({
        ...prev,
        showResponseSugestionsPopup: false,
      }));
    }
  };

  const showSuggestionsPopup = () => {
    setState((prev) => ({
      ...prev,
      showResponseSugestionsPopup: true,
    }));
  };
  const closeSuggestionsPopup = () => {
    setState((prev) => ({
      ...prev,
      showResponseSugestionsPopup: false,
    }));
  };

  return (
    <div>
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          actions: {
            handleUserMessage,
            handleUserAction, // This is the response handler for suggested responses and voice inputs
          },
        });
      })}
    </div>
  );
};

export default ActionProvider;
